import React, {
  FC,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import qs from "query-string";
import { useAppSelector } from "@/redux/store";
import { Product } from "@/services/productService";
import ContentImage from "@/components/content/contentImage";
import { ItemDescription } from "../itemDescription";
import ProductSizes from "../sizes";
import { SaleWishList } from "@/components/dynamic/dynamic-sale/SaleWishList";
import AppOnlyItemInfoModal from "../appOnlyItemInfoModal";
import { selectCdnImageSettings } from "@/util/selectors";
import { isMobile, isTabletView } from "@/util";
import PlpAnalytics from "@/services/analytics/main/plp";
import { useRouteSlug } from "@/hooks";
import { AlgoliaInsightsService } from "@/services";
import { algoliaPLPContext } from "../productListingPage";
import "./style.scss";

const GRID_CLASS_NAMES = {
  4: "four-view",
  6: "six-view"
};

type ListItemProps = {
  item: Product;
  position: number;
  listHeading?: string;
  toggleQuicklook: (
    toggleState: boolean,
    selectedIndex: number,
    event?: React.MouseEvent<HTMLElement>
  ) => void;
};

const ListItem: FC<ListItemProps> = ({
  item,
  position,
  listHeading,
  toggleQuicklook
}) => {
  const { t } = useTranslation("productListing");
  const [toggleAppOnlyItemModal, setToggleAppOnlyItemModal] = useState(false);
  const gridValue = useAppSelector(
    state => state.productListingReducer.gridValue
  );
  const routeSlug = useRouteSlug();
  const configCdnImagesSettings = useAppSelector(selectCdnImageSettings);
  const { algoliaIndex, algoliaQueryId, algoliaOrigin } =
    useContext(algoliaPLPContext);

  if (!item) return null;

  const onClick = useCallback(() => {
    PlpAnalytics.select({
      item: { ...item, position },
      listHeading
    });

    AlgoliaInsightsService.trackProductClick({
      productId: item.id,
      position: position + 1,
      queryID: algoliaQueryId,
      origin: algoliaOrigin,
      algoliaIndex
    });
  }, [
    item,
    position,
    listHeading,
    algoliaQueryId,
    algoliaOrigin,
    algoliaIndex
  ]);

  const onQuicklookClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
      event.preventDefault();
      if (item.isAppOnlyPurchase) {
        setToggleAppOnlyItemModal(true);
        return;
      }
      PlpAnalytics.select({
        item: { ...item, position },
        listHeading
      });

      toggleQuicklook(true, position, event);
    },
    [item, position, listHeading, toggleQuicklook]
  );

  const isMobileTabletView = useMemo(
    () => Boolean(isMobile.any()) || isTabletView(),
    []
  );

  return (
    <li className={cn("listing_grid", "list", GRID_CLASS_NAMES[gridValue])}>
      <Link
        onClick={onClick}
        to={{
          pathname: `/${routeSlug}/${item.url}`,
          state: { fromPLP: true },
          search: qs.stringifyUrl(
            {
              url: "",
              query: {
                queryId: algoliaQueryId,
                origin: algoliaOrigin
              }
            },
            { skipEmptyString: true }
          )
        }}
        className="hover_view_content"
      >
        <div
          className={cn("position-relative", {
            hover_layer: !isMobileTabletView
          })}
        >
          <div
            id="hover_view_product_image"
            className={cn("hover_view", {
              isMobileDevice: isMobileTabletView
            })}
          >
            <div className={`listing_image_wrap ${item.id}`}>
              <div className="listing_hover_content">
                {item.isPriceAvailableInUserCountry && (
                  <SaleWishList product={item} />
                )}
                <span className="quickview_btn" onClick={onQuicklookClick}>
                  {t("quickLook")}
                </span>
              </div>
              <ContentImage
                img={item.captionImageURL}
                url={configCdnImagesSettings?.list}
                overlayLabel={item.overlayTagLabel?.label}
                overlayTagBgColor={item.overlayTagLabel?.style?.backgroundColor}
                overlayTagLabelColor={item.overlayTagLabel?.style?.textColor}
                showDiscountSlab
                item={item}
                imagePlaceholderType="PLP"
                alt={item.title?.defaultValue ?? ""}
              />
            </div>
          </div>
        </div>
        <ProductSizes sizes={item.displaySizes} gridSize={gridValue} />
        <ItemDescription item={item} />
      </Link>
      {item.isAppOnlyPurchase && (
        <AppOnlyItemInfoModal
          toggleModal={toggleAppOnlyItemModal}
          setToggleModal={setToggleAppOnlyItemModal}
          product={item}
        />
      )}
    </li>
  );
};

export default memo(ListItem);
